import { AccountsService } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { UnreachableError } from 'base/helpers';
import { LibraryService } from 'base/library_service';
import { PreferencesStore } from 'base/preferences_store';
import { CrossFade } from 'base/ui/cross_fade';
import { useDocumentTitle } from 'base/ui/document_title';
import { IconQuestion } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import logoImage from 'base/ui/logo.file.svg';
import { useAnchoredMenu } from 'base/ui/menu';
import { menuItem, MenuModel } from 'base/ui/menu_model';
import { Scrollable } from 'base/ui/scrollable';
import { ToastLayer, ToastQueue } from 'base/ui/toast';
import { Title5 } from 'base/ui/typography';
import { BlocksService } from 'blocks/blocks_service';
import { Footer } from 'blocks/dashboard/footer';
import { MaybeGrowthNudge } from 'blocks/dashboard/growth_nudges';
import { MaybeLegacy2024PricingModal } from 'blocks/dashboard/MaybeLegacy2024PricingModal';
import { Sidebar } from 'blocks/dashboard/sidebar';
import { TeamActions } from 'blocks/dashboard/team_actions';
import { UpgradeBanner } from 'blocks/dashboard/UpgradeBanner';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { CouponBanners } from 'blocks/manage_account/coupon_banners';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { MaybeAnnouncementDialog } from './announcement';
import { NavImportSitemap, NavNewProject, NavSearch, NavUser } from './nav';
import styles from './page.css';
import { PricingParityBanner } from './pricing_parity';
import { ProjectsList } from './projects_list/projects_list';
import { LoggedOutProjectsListState } from './projects_list/states';
import { getProjectsDataStore } from './project_data/projects_data_store_manager';
import { DashboardRoute, dashboardRouteToUrl } from './routes';
import { SearchResults } from './search';

export const DashboardPage = observer(function _DashboardPage({
  toastQueue,
  accountStore,
  featureFlagsStore,
  preferencesStore,
  libraryService,
  blocksService,
  accountsService,
  analytics,
  isLoggedIn,
  route,
}: {
  toastQueue: ToastQueue;
  accountStore: AccountStore;
  featureFlagsStore: FeatureFlagsStore;
  preferencesStore: PreferencesStore;
  libraryService: LibraryService;
  blocksService: BlocksService;
  accountsService: AccountsService;
  analytics: AnalyticsService;
  isLoggedIn: boolean;
  route: DashboardRoute;
}) {
  const title = getTitle(route, accountStore);
  useDocumentTitle(`${title} - Relume`);

  const dashboardHome = dashboardRouteToUrl({ path: 'dashboard' });

  const owner = route.projectsFilter || 'user';

  const projectListsStore = getProjectsDataStore({
    blocksService,
    accountStore,
    analytics,
    toastQueue,
    ownershipType: route.projectsFilter,
  });

  const showTeamActions =
    accountStore.member?.productRestrictions.teamEnabled &&
    route.projectsFilter == 'team';

  return (
    <DashboardSkeleton
      logoHref={
        dashboardRouteToUrl(route) === dashboardHome ? '/' : dashboardHome
      }
      nav={
        <Inline spacing={16} yalign="center">
          <NavSearch
            renderResults={(query, mode) => (
              <SearchResults
                query={query}
                mode={mode}
                toastQueue={toastQueue}
                libraryService={libraryService}
                blocksService={blocksService}
                analytics={analytics}
              />
            )}
          />
          <div />
          <NavImportSitemap
            owner={owner}
            accountStore={accountStore}
            projectsListStore={projectListsStore}
            blocksService={blocksService}
            analytics={analytics}
          />
          <NavNewProject
            owner={owner}
            projectsListStore={projectListsStore}
            accountStore={accountStore}
            blocksService={blocksService}
            analytics={analytics}
          />
          <NavUser accountStore={accountStore} />
        </Inline>
      }
      sidebar={
        <Sidebar
          toastQueue={toastQueue}
          route={route}
          featureFlagsStore={featureFlagsStore}
          accountStore={accountStore}
          accountsService={accountsService}
          analytics={analytics}
        />
      }
      footer={<Footer accountStore={accountStore} />}
    >
      <CrossFade transitionKey={route.path}>
        {isLoggedIn ? (
          <Rows spacing={16}>
            <Inline xalign="space-between">
              <Title5>{title}</Title5>
              {showTeamActions && (
                <TeamActions
                  accountStore={accountStore}
                  accountsService={accountsService}
                />
              )}
            </Inline>
            <Rows spacing={32}>
              <CouponBanners
                accountsService={accountsService}
                analytics={analytics}
                analyticsLocation="projects-list"
              />
              <PricingParityBanner accountStore={accountStore} />
              {accountStore.member && (
                <UpgradeBanner
                  accountStore={accountStore}
                  projectsListStore={projectListsStore}
                  analytics={analytics}
                />
              )}
              <ProjectsList
                accountStore={accountStore}
                projectsListStore={projectListsStore}
                filterOwnership={route.projectsFilter}
                blocksService={blocksService}
                accountsService={accountsService}
                toastQueue={toastQueue}
                analytics={analytics}
              />
            </Rows>
          </Rows>
        ) : (
          <LoggedOutProjectsListState />
        )}
      </CrossFade>
      <ToastLayer queue={toastQueue} />
      <MaybeAnnouncementDialog accountStore={accountStore} />
      <HelpButton />
      <MaybeLegacy2024PricingModal
        accountStore={accountStore}
        preferencesStore={preferencesStore}
      />
      <MaybeGrowthNudge
        growthNudgeKey="contactSales"
        accountsService={accountsService}
      />
    </DashboardSkeleton>
  );
});

function getTitle(route: DashboardRoute, accountStore: AccountStore) {
  switch (route.path) {
    case 'dashboard':
      switch (route.projectsFilter) {
        case 'user':
          return 'My Projects';
        case 'team':
          const teamName = accountStore.member?.team?.name || 'Team';
          return `${teamName} Projects`;
        case undefined:
          return 'Recent Projects';
        default:
          throw new UnreachableError(route.projectsFilter);
      }
    default:
      throw new UnreachableError(route.path);
  }
}

function DashboardSkeleton({
  logoHref,
  nav,
  sidebar,
  footer,
  children,
}: {
  logoHref: string;
  nav: React.ReactNode;
  sidebar: React.ReactNode;
  footer: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.page}>
      <div className={styles.pageInner}>
        <div className={styles.logo}>
          <a href={logoHref} className={styles.logoLink}>
            <img
              src={logoImage}
              className={styles.logoImage}
              alt="Relume Library"
              role="presentation"
            />
          </a>
        </div>
        <nav className={styles.nav}>{nav}</nav>
        <div className={styles.sidebar}>
          <Scrollable backgroundColor="colorLightGrey3">
            <div className={styles.sidebarInner}>{sidebar}</div>
          </Scrollable>
        </div>
        <div className={styles.footer}>{footer}</div>
        <main className={styles.content}>
          <Scrollable backgroundColor="colorLightGrey3">
            <div className={styles.contentInner}>
              <div className={styles.contentInnerInner}>{children}</div>
            </div>
          </Scrollable>
        </main>
      </div>
    </div>
  );
}

const menu: MenuModel = [
  menuItem({
    label: 'Learn',
    href: 'https://relume.io/resources',
    target: '_blank',
  }),
  menuItem({
    label: 'FAQs',
    href: 'https://relume.io/resources/faqs',
    target: '_blank',
  }),
  menuItem({
    label: 'Troubleshooting',
    href: 'https://relume.io/troubleshooting',
    target: '_blank',
  }),
  menuItem({
    label: 'Support',
    href: 'https://www.relume.io/support',
    target: '_self',
  }),
];

function HelpButton() {
  const [isOpen, setIsOpenState] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const close = React.useCallback(() => {
    setIsOpenState(false);
  }, []);
  const onClickOrContextMenu = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (isOpen) close();
      else setIsOpenState(true);
    },
    [isOpen, close],
  );

  const [portal] = useAnchoredMenu(isOpen, close, buttonRef, menu, {
    search: 'hidden',
  });

  return (
    <div className={styles.helpButtonContainer}>
      <button
        ref={buttonRef}
        onClick={onClickOrContextMenu}
        onContextMenu={onClickOrContextMenu}
        className={styles.helpButton}
      >
        <IconQuestion className={styles.helpButtonIcon} />
      </button>
      {portal}
    </div>
  );
}
